import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { apiGet, apiDelete, apiPost } from '../utils/api';
import { API } from '../config';
import { useTheme } from '../context/ThemeContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ResponsiveLogo } from '../components/ThemeLogo';
import { toast } from 'sonner';
import {
  ArrowLeft, Sun, Moon, ChevronRight, Upload, Trash2, BookOpen,
  Brain, Target, Lightbulb, AlertTriangle, BarChart3, Loader2,
  Search, ChevronDown, ChevronUp, ExternalLink, Zap, RefreshCw,
  CheckCircle2, XCircle, FileText, Clock, X,
} from 'lucide-react';

/* ─── Processing stage config ─── */
const STAGE_CONFIG = {
  queued:         { label: 'Queued',                 icon: Clock,        color: 'text-slate-400',  bg: 'bg-slate-500/10',  pct: 3   },
  pending:        { label: 'Queued',                 icon: Clock,        color: 'text-slate-400',  bg: 'bg-slate-500/10',  pct: 3   },
  claimed:        { label: 'Starting...',            icon: Loader2,      color: 'text-blue-400',   bg: 'bg-blue-500/10',   pct: 8   },
  parsing:        { label: 'Parsing Document',       icon: FileText,     color: 'text-blue-500',   bg: 'bg-blue-500/10',   pct: 20  },
  extracting:     { label: 'Extracting Knowledge',   icon: Brain,        color: 'text-amber-500',  bg: 'bg-amber-500/10',  pct: 55  },
  building_packs: { label: 'Building Knowledge Packs', icon: Target,    color: 'text-violet-500', bg: 'bg-violet-500/10', pct: 85  },
  completed:      { label: 'Complete',               icon: CheckCircle2, color: 'text-emerald-500',bg: 'bg-emerald-500/10',pct: 100 },
  failed:         { label: 'Failed',                 icon: XCircle,      color: 'text-red-500',    bg: 'bg-red-500/10',    pct: 0   },
};

/* ─── Stat Card ─── */
function StatCard({ icon: Icon, label, value, color }) {
  return (
    <div className="p-4 rounded-xl border border-border bg-card" data-testid={`stat-${label.toLowerCase().replace(/ /g, '-')}`}>
      <div className="flex items-center gap-2 mb-1">
        <Icon className={`w-4 h-4 ${color}`} />
        <span className="text-xs text-muted-foreground">{label}</span>
      </div>
      <p className="text-2xl font-bold">{value}</p>
    </div>
  );
}

/* ─── Processing Job Card ─── */
function ProcessingJobCard({ job, onRetry, onDismiss }) {
  const stage = STAGE_CONFIG[job.status] || STAGE_CONFIG.pending;
  const StageIcon = stage.icon;
  const isActive = ['queued', 'pending', 'claimed', 'parsing', 'extracting', 'building_packs'].includes(job.status);
  const chunkInfo = job.total_chunks > 0 && job.completed_chunks !== undefined
    ? ` (${job.completed_chunks}/${job.total_chunks} sections)`
    : '';

  return (
    <div className={`rounded-xl border border-border bg-card p-4 ${isActive ? 'ring-1 ring-odapto-orange/30' : ''}`} data-testid={`job-${job.job_id}`}>
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <StageIcon className={`w-4 h-4 shrink-0 ${stage.color} ${isActive ? 'animate-pulse' : ''}`} />
          <span className="font-medium text-sm truncate">{job.title || 'Processing...'}</span>
        </div>
        <div className="flex gap-1 shrink-0">
          {job.status === 'failed' && (
            <button onClick={() => onRetry(job.job_id)} className="p-1 rounded hover:bg-muted transition-colors" title="Retry" data-testid={`retry-${job.job_id}`}>
              <RefreshCw className="w-3.5 h-3.5 text-odapto-orange" />
            </button>
          )}
          {(job.status === 'completed' || job.status === 'failed') && (
            <button onClick={() => onDismiss(job.job_id)} className="p-1 rounded hover:bg-muted transition-colors" title="Dismiss">
              <X className="w-3.5 h-3.5 text-muted-foreground" />
            </button>
          )}
        </div>
      </div>

      {/* Progress bar */}
      <div className="w-full h-1.5 bg-muted rounded-full overflow-hidden mb-1.5">
        <div
          className={`h-full rounded-full transition-all duration-700 ease-out ${
            job.status === 'failed' ? 'bg-red-500' :
            job.status === 'completed' ? 'bg-emerald-500' : 'bg-odapto-orange'
          }`}
          style={{ width: `${isActive ? Math.max(stage.pct, (job.completed_chunks / Math.max(job.total_chunks, 1)) * 90 + 10) : stage.pct}%` }}
        />
      </div>

      <div className="flex items-center justify-between">
        <span className={`text-xs font-medium ${stage.color}`}>{stage.label}{chunkInfo}</span>
        {job.progress && job.status !== 'completed' && (
          <span className="text-[10px] text-muted-foreground truncate ml-2 max-w-[200px]">{job.progress}</span>
        )}
        {job.status === 'completed' && job.stats && (
          <span className="text-[10px] text-emerald-500">{job.stats.total_items} items extracted</span>
        )}
      </div>

      {job.status === 'failed' && job.error && (
        <p className="text-xs text-red-400 mt-1.5 line-clamp-2">{job.error}</p>
      )}

      {/* Retry count & worker info */}
      {(job.retry_count > 0 || job.worker_id) && (
        <div className="flex items-center gap-3 mt-1.5 text-[10px] text-muted-foreground">
          {job.retry_count > 0 && <span>Attempt {job.retry_count + 1}</span>}
          {job.worker_id && isActive && <span>Worker: {job.worker_id}</span>}
        </div>
      )}
    </div>
  );
}

/* ─── Upload Progress Overlay ─── */
function UploadProgressBar({ progress, fileName }) {
  return (
    <div className="rounded-xl border border-odapto-orange/30 bg-card p-4 ring-1 ring-odapto-orange/20" data-testid="upload-progress">
      <div className="flex items-center gap-2 mb-2">
        <Upload className="w-4 h-4 text-odapto-orange animate-pulse" />
        <span className="font-medium text-sm truncate">Uploading: {fileName}</span>
      </div>
      <div className="w-full h-2 bg-muted rounded-full overflow-hidden mb-1">
        <div
          className="h-full rounded-full bg-odapto-orange transition-all duration-300"
          style={{ width: `${progress}%` }}
        />
      </div>
      <span className="text-xs text-muted-foreground">{progress}% uploaded</span>
    </div>
  );
}

/* ─── Knowledge Pack Card ─── */
function KnowledgePackCard({ pack, onView, onDelete }) {
  const stats = pack.stats || {};
  return (
    <div className="rounded-xl border border-border bg-card overflow-hidden hover:shadow-md transition-shadow" data-testid={`pack-${pack.book_id}`}>
      <div className="p-5">
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1 min-w-0">
            <h3 className="font-bold text-base truncate">{pack.title}</h3>
            <p className="text-sm text-muted-foreground">{pack.author}</p>
          </div>
          <div className="flex gap-1 shrink-0">
            <button onClick={() => onView(pack)} className="p-1.5 rounded-lg hover:bg-muted transition-colors" title="View" data-testid={`view-${pack.book_id}`}>
              <ExternalLink className="w-4 h-4" />
            </button>
            <button onClick={() => onDelete(pack)} className="p-1.5 rounded-lg hover:bg-muted text-red-500 transition-colors" title="Delete" data-testid={`delete-${pack.book_id}`}>
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>
        <div className="flex flex-wrap gap-1.5 mb-3">
          {(pack.domains || []).map(d => (
            <span key={d} className="text-[10px] px-2 py-0.5 rounded-full bg-odapto-orange/10 text-odapto-orange font-medium">{d}</span>
          ))}
        </div>
        <div className="grid grid-cols-4 gap-2 text-center">
          {[
            { label: 'Frameworks', val: stats.frameworks || 0 },
            { label: 'Models', val: stats.mental_models || 0 },
            { label: 'Rules', val: stats.decision_rules || 0 },
            { label: 'KPIs', val: stats.kpis || 0 },
          ].map(s => (
            <div key={s.label} className="p-1.5 rounded-lg bg-muted/50">
              <p className="text-lg font-bold">{s.val}</p>
              <p className="text-[9px] text-muted-foreground">{s.label}</p>
            </div>
          ))}
        </div>
        <div className="flex items-center justify-between mt-3 pt-3 border-t border-border">
          <span className="text-[10px] text-muted-foreground">{stats.total_items || 0} total knowledge items</span>
          <span className="text-[10px] text-muted-foreground">{pack.created_at ? new Date(pack.created_at).toLocaleDateString() : ''}</span>
        </div>
      </div>
    </div>
  );
}

/* ─── Knowledge Detail View ─── */
function KnowledgeDetail({ pack, onBack }) {
  const [expanded, setExpanded] = useState({
    frameworks: true, mental_models: false, decision_rules: false,
    diagnostic_questions: false, warning_signs: false, kpis: false,
    executive_recommendations: false, key_concepts: false,
  });
  const knowledge = pack.knowledge || {};
  const sections = [
    { key: 'frameworks', label: 'Frameworks', icon: Target, items: knowledge.frameworks || [] },
    { key: 'mental_models', label: 'Mental Models', icon: Brain, items: knowledge.mental_models || [] },
    { key: 'decision_rules', label: 'Decision Rules', icon: Zap, items: knowledge.decision_rules || [] },
    { key: 'diagnostic_questions', label: 'Diagnostic Questions', icon: Lightbulb, items: knowledge.diagnostic_questions || [] },
    { key: 'warning_signs', label: 'Warning Signs', icon: AlertTriangle, items: knowledge.warning_signs || [] },
    { key: 'kpis', label: 'KPIs', icon: BarChart3, items: knowledge.kpis || [] },
    { key: 'executive_recommendations', label: 'Executive Recommendations', icon: Target, items: knowledge.executive_recommendations || [] },
    { key: 'key_concepts', label: 'Key Concepts', icon: BookOpen, items: knowledge.key_concepts || [] },
  ];

  return (
    <div data-testid="knowledge-detail">
      <button onClick={onBack} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-4" data-testid="back-to-library">
        <ArrowLeft className="w-4 h-4" /> Back to Library
      </button>
      <div className="mb-6">
        <h2 className="text-xl font-bold">{pack.title}</h2>
        <p className="text-sm text-muted-foreground">{pack.author}</p>
        <div className="flex flex-wrap gap-1.5 mt-2">
          {(pack.domains || []).map(d => (
            <span key={d} className="text-xs px-2.5 py-0.5 rounded-full bg-odapto-orange/10 text-odapto-orange font-medium">{d}</span>
          ))}
        </div>
      </div>
      <div className="space-y-3">
        {sections.filter(s => s.items.length > 0).map(section => {
          const Icon = section.icon;
          const isOpen = expanded[section.key];
          return (
            <div key={section.key} className="rounded-xl border border-border bg-card overflow-hidden">
              <button
                onClick={() => setExpanded(prev => ({ ...prev, [section.key]: !prev[section.key] }))}
                className="w-full flex items-center gap-3 p-4 hover:bg-muted/30 transition-colors text-left"
              >
                <Icon className="w-5 h-5 text-odapto-orange shrink-0" />
                <span className="font-semibold text-sm flex-1">{section.label}</span>
                <span className="text-xs text-muted-foreground mr-2">{section.items.length}</span>
                {isOpen ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
              </button>
              {isOpen && (
                <div className="border-t border-border divide-y divide-border max-h-96 overflow-y-auto">
                  {section.items.map((item, i) => (
                    <div key={i} className="px-4 py-3 text-sm">
                      {item.name && <p className="font-medium text-odapto-orange mb-0.5">{item.name}</p>}
                      {item.rule && <p className="font-medium mb-0.5">{item.rule}</p>}
                      {item.question && <p className="font-medium mb-0.5">{item.question}</p>}
                      {item.sign && <p className="font-medium mb-0.5">{item.sign}</p>}
                      {item.concept && <p className="font-medium text-odapto-orange mb-0.5">{item.concept}</p>}
                      {item.recommendation && <p className="font-medium mb-0.5">{item.recommendation}</p>}
                      {item.description && <p className="text-muted-foreground text-xs">{item.description}</p>}
                      {item.insight && <p className="text-muted-foreground text-xs">{item.insight}</p>}
                      {item.context && <p className="text-muted-foreground text-xs">{item.context}</p>}
                      {item.purpose && <p className="text-muted-foreground text-xs">{item.purpose}</p>}
                      {item.meaning && <p className="text-muted-foreground text-xs">{item.meaning}</p>}
                      {item.definition && <p className="text-muted-foreground text-xs">{item.definition}</p>}
                      {item.impact && <p className="text-xs text-emerald-500">{item.impact}</p>}
                      {item.application && <p className="text-xs text-muted-foreground italic">{item.application}</p>}
                      {item.when_to_use && <p className="text-xs text-muted-foreground">When: {item.when_to_use}</p>}
                      {item.action && <p className="text-xs text-odapto-orange">Action: {item.action}</p>}
                      {item.when_critical && <p className="text-xs text-muted-foreground">Critical when: {item.when_critical}</p>}
                      {item.priority && <span className={`text-[10px] px-1.5 py-0.5 rounded ${item.priority === 'High' ? 'bg-red-500/10 text-red-500' : item.priority === 'Medium' ? 'bg-amber-500/10 text-amber-500' : 'bg-blue-500/10 text-blue-500'}`}>{item.priority}</span>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}


/* ═══════════════════════════════════════
   MAIN PAGE
   ═══════════════════════════════════════ */
export default function AdminIntelligencePage() {
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const fileRef = useRef(null);

  const [view, setView] = useState('list');
  const [packs, setPacks] = useState([]);
  const [libraryStats, setLibraryStats] = useState({ total_books: 0, total_knowledge_items: 0 });
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [selectedPack, setSelectedPack] = useState(null);

  // Upload state
  const [uploadProgress, setUploadProgress] = useState(null); // null = not uploading, 0-100 = %
  const [uploadFileName, setUploadFileName] = useState('');

  // Active processing jobs
  const [activeJobs, setActiveJobs] = useState([]);
  const [dismissedJobs, setDismissedJobs] = useState(new Set());

  const fetchLibrary = useCallback(async () => {
    setLoading(true);
    const params = searchQuery ? `?search=${encodeURIComponent(searchQuery)}` : '';
    const res = await apiGet(`/intelligence/library${params}`);
    if (res.ok) {
      const d = await res.json();
      setPacks(d.packs || []);
      setLibraryStats(d.library_stats || {});
    }
    setLoading(false);
  }, [searchQuery]);

  const fetchActiveJobs = useCallback(async () => {
    const res = await apiGet('/intelligence/library/active-jobs');
    if (res.ok) {
      const d = await res.json();
      setActiveJobs(d.jobs || []);
    }
  }, []);

  useEffect(() => { fetchLibrary(); fetchActiveJobs(); }, [fetchLibrary, fetchActiveJobs]);

  // Poll active jobs
  useEffect(() => {
    const hasActive = activeJobs.some(j =>
      ['queued', 'pending', 'claimed', 'parsing', 'extracting', 'building_packs'].includes(j.status)
      && !dismissedJobs.has(j.job_id)
    );
    if (!hasActive) return;

    const interval = setInterval(async () => {
      const res = await apiGet('/intelligence/library/active-jobs');
      if (res.ok) {
        const d = await res.json();
        const newJobs = d.jobs || [];
        setActiveJobs(newJobs);

        // Check if any job just completed
        const justCompleted = newJobs.filter(j => j.status === 'completed');
        if (justCompleted.length > 0) {
          fetchLibrary();
        }
      }
    }, 3000);
    return () => clearInterval(interval);
  }, [activeJobs, dismissedJobs, fetchLibrary]);

  /* ─── Upload with XHR for progress ─── */
  const handleUpload = useCallback((e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const ext = file.name.split('.').pop().toLowerCase();
    if (!['pdf', 'docx', 'txt', 'md', 'csv'].includes(ext)) {
      toast.error('Unsupported file type. Use PDF, DOCX, or TXT.');
      return;
    }

    const maxSize = 250 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error(`File too large (${(file.size / 1024 / 1024).toFixed(0)} MB). Maximum is 250 MB.`);
      return;
    }

    setUploadFileName(file.name);
    setUploadProgress(0);

    const formData = new FormData();
    formData.append('file', file);

    const xhr = new XMLHttpRequest();
    const token = localStorage.getItem('odapto_session_token') || sessionStorage.getItem('odapto_session_token');

    xhr.upload.addEventListener('progress', (evt) => {
      if (evt.lengthComputable) {
        setUploadProgress(Math.round((evt.loaded / evt.total) * 100));
      }
    });

    xhr.addEventListener('load', () => {
      setUploadProgress(null);
      setUploadFileName('');
      if (fileRef.current) fileRef.current.value = '';

      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          const data = JSON.parse(xhr.responseText);
          toast.success(`"${data.title}" uploaded. Processing started.`);
          fetchActiveJobs();
        } catch {
          toast.success('Upload successful. Processing started.');
          fetchActiveJobs();
        }
      } else if (xhr.status === 409) {
        try {
          const err = JSON.parse(xhr.responseText);
          toast.error(err.detail || 'Duplicate file detected.');
        } catch {
          toast.error('This file has already been uploaded.');
        }
      } else if (xhr.status === 413) {
        toast.error('File too large for the server to accept.');
      } else {
        try {
          const err = JSON.parse(xhr.responseText);
          toast.error(err.detail || 'Upload failed.');
        } catch {
          toast.error('Upload failed. Please try again.');
        }
      }
    });

    xhr.addEventListener('error', () => {
      setUploadProgress(null);
      setUploadFileName('');
      if (fileRef.current) fileRef.current.value = '';
      toast.error('Connection error during upload.');
    });

    xhr.addEventListener('abort', () => {
      setUploadProgress(null);
      setUploadFileName('');
      if (fileRef.current) fileRef.current.value = '';
    });

    xhr.open('POST', `${API}/intelligence/library/upload`);
    xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    xhr.send(formData);
  }, [fetchActiveJobs]);

  /* ─── Retry ─── */
  const handleRetry = useCallback(async (jobId) => {
    const res = await apiPost(`/intelligence/library/retry/${jobId}`);
    if (res.ok) {
      toast.success('Retrying extraction...');
      fetchActiveJobs();
    } else {
      const err = await res.json().catch(() => ({}));
      toast.error(err.detail || 'Retry failed');
    }
  }, [fetchActiveJobs]);

  const handleDismiss = useCallback((jobId) => {
    setDismissedJobs(prev => new Set([...prev, jobId]));
  }, []);

  const handleView = async (pack) => {
    const res = await apiGet(`/intelligence/library/${pack.book_id}`);
    if (res.ok) {
      setSelectedPack(await res.json());
      setView('detail');
    } else toast.error('Failed to load');
  };

  const handleDelete = async (pack) => {
    if (!window.confirm(`Delete "${pack.title}" from the library?`)) return;
    const res = await apiDelete(`/intelligence/library/${pack.book_id}`);
    if (res.ok) { toast.success('Deleted'); fetchLibrary(); fetchActiveJobs(); }
    else toast.error('Failed to delete');
  };

  // Visible jobs = non-dismissed, recent (last 20)
  const visibleJobs = activeJobs.filter(j => !dismissedJobs.has(j.job_id));
  const hasActiveProcessing = visibleJobs.some(j =>
    ['queued', 'pending', 'claimed', 'parsing', 'extracting', 'building_packs'].includes(j.status)
  );

  if (view === 'detail' && selectedPack) {
    return (
      <div className="min-h-screen bg-background">
        <Header theme={theme} toggleTheme={toggleTheme} navigate={navigate} />
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6">
          <KnowledgeDetail pack={selectedPack} onBack={() => { setView('list'); setSelectedPack(null); }} />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="admin-intelligence-page">
      <Header theme={theme} toggleTheme={toggleTheme} navigate={navigate} />

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6">
        {/* Title + Upload Button */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold">Strategic Knowledge Library</h1>
            <p className="text-sm text-muted-foreground">Upload business books to power executive-level AI reasoning</p>
          </div>
          <div>
            <input ref={fileRef} type="file" accept=".pdf,.docx,.txt,.md" onChange={handleUpload} className="hidden" data-testid="file-input" />
            <Button
              onClick={() => fileRef.current?.click()}
              disabled={uploadProgress !== null}
              className="bg-odapto-orange hover:bg-odapto-orange-hover text-white"
              data-testid="upload-book-btn"
            >
              {uploadProgress !== null
                ? <Loader2 className="w-4 h-4 animate-spin mr-2" />
                : <Upload className="w-4 h-4 mr-2" />
              }
              Upload Book
            </Button>
          </div>
        </div>

        {/* Upload Progress */}
        {uploadProgress !== null && (
          <div className="mb-4">
            <UploadProgressBar progress={uploadProgress} fileName={uploadFileName} />
          </div>
        )}

        {/* Active Processing Jobs */}
        {visibleJobs.length > 0 && (
          <div className="mb-6" data-testid="active-jobs-panel">
            <div className="flex items-center gap-2 mb-3">
              {hasActiveProcessing && <Loader2 className="w-4 h-4 animate-spin text-odapto-orange" />}
              <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">Processing Queue</h2>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              {visibleJobs.map(job => (
                <ProcessingJobCard key={job.job_id} job={job} onRetry={handleRetry} onDismiss={handleDismiss} />
              ))}
            </div>
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6" data-testid="library-stats">
          <StatCard icon={BookOpen} label="Books" value={libraryStats.total_books} color="text-odapto-orange" />
          <StatCard icon={Brain} label="Knowledge Items" value={libraryStats.total_knowledge_items} color="text-violet-500" />
          <StatCard icon={Target} label="Domains" value={packs.reduce((s, p) => { (p.domains || []).forEach(d => s.add(d)); return s; }, new Set()).size} color="text-blue-500" />
          <StatCard icon={Zap} label="Active Packs" value={packs.length} color="text-emerald-500" />
        </div>

        {/* Search */}
        <div className="mb-4">
          <div className="relative max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Search library..." className="pl-9" data-testid="search-input" />
          </div>
        </div>

        {/* Library Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-odapto-orange" /></div>
        ) : packs.length === 0 ? (
          <div className="text-center py-16 border border-border rounded-xl bg-card" data-testid="empty-library">
            <BookOpen className="w-12 h-12 text-muted-foreground/30 mx-auto mb-3" />
            <h3 className="font-semibold mb-1">No knowledge packs yet</h3>
            <p className="text-sm text-muted-foreground mb-4">Upload your first business book to build the Strategic Knowledge Library.</p>
            <Button onClick={() => fileRef.current?.click()} className="bg-odapto-orange hover:bg-odapto-orange-hover text-white" data-testid="upload-first-book-btn">
              <Upload className="w-4 h-4 mr-2" /> Upload Your First Book
            </Button>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="packs-grid">
            {packs.map(pack => (
              <KnowledgePackCard key={pack.book_id} pack={pack} onView={handleView} onDelete={handleDelete} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/* ─── Header ─── */
function Header({ theme, toggleTheme, navigate }) {
  return (
    <header className="sticky top-0 z-50 bg-background/80 backdrop-blur-xl border-b border-border">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Link to="/dashboard"><ResponsiveLogo className="h-8 w-auto" /></Link>
          <ChevronRight className="w-4 h-4 text-muted-foreground" />
          <span className="text-sm font-medium">Admin</span>
          <ChevronRight className="w-4 h-4 text-muted-foreground" />
          <span className="text-sm text-muted-foreground">Strategic Intelligence</span>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" onClick={toggleTheme}>
            {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
          </Button>
          <Button variant="ghost" size="sm" onClick={() => navigate('/admin')}>
            <ArrowLeft className="w-4 h-4 mr-1" /> Admin
          </Button>
        </div>
      </div>
    </header>
  );
}
